import anime from 'animejs';
import classNames from 'classnames';
import throttle from 'lodash/throttle';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import EMOJI_REACTIONS_MAP from 'constants/emojiReactions';
import { ROLLOUT_NEW_ANONYMOUS_REACTION_NAME_INPUT } from 'constants/featureFlag';
import { getFeatureFlagCreator } from 'creators/featureFlag';
import {
  createReaction,
  updateUsername,
  videoPlayerUpdateAnonymousReactionMode,
} from 'creators/videoPlayer';
import { getAnonUserName } from 'utilities/auth';

import HideDuringDowntime from '../../hide-during-downtime';

import CommentButton from 'components/ui-components/comment-button';
import Emoji from 'components/ui-components/emoji';
import Transition from 'components/ui-components/transition';

import './styles.less';

const EMOJI_BUTTON_SMASHING_DELAY = 500;
const VIDEO_PLAYER_WIDTH_HIDE_EMOJIS_ON_VOLUME_ACTIVE = 650;

function isInvalidName(name) {
  return /[@!\$|<>]+/.test(name);
}

class EmojiBar extends Component {
  constructor(props) {
    super(props);

    props.getFeatureFlag(ROLLOUT_NEW_ANONYMOUS_REACTION_NAME_INPUT);

    this.state = {
      time: 0,
      showUsernameInput: false,
      showEmojiBar: true,
      volumeControlOverEmojiBar: false,
    };
  }

  render() {
    const {
      hideReaction,
      duration,
      controlsActive,
      drawnInsidePlayer,
      commentsEnabled,
      emojiEnabled,
    } = this.props;
    const { volumeControlOverEmojiBar, showInvalidUsername } = this.state;

    return (
      <HideDuringDowntime>
        <div
          className={classNames('theme-dark', 'emoji-bar-container', {
            'controls-not-active': !controlsActive,
            hide: volumeControlOverEmojiBar,
          })}
        >
          {this.props.children}
          {this.state.showEmojiBar && (
            <div className="emoji-bar" ref={me => (this.emojiBar = me)}>
              {emojiEnabled &&
                Object.values(EMOJI_REACTIONS_MAP).map(type => (
                  <Emoji
                    dataTag={`${type}-reaction-button`}
                    key={type}
                    duration={duration}
                    time={Number(this.state.time)}
                    show={hideReaction != type}
                    type={type}
                    onClick={throttle(
                      this.createEmojiReaction,
                      EMOJI_BUTTON_SMASHING_DELAY
                    )}
                  />
                ))}
              {commentsEnabled && (
                <CommentButton
                  onEmbedPlayer={drawnInsidePlayer}
                  buttonText={emojiEnabled ? null : 'Leave a Comment'}
                />
              )}
            </div>
          )}
          <Transition
            show={this.state.showUsernameInput}
            onDidMount={this.showUsername}
            onWillUnmount={this.hideUsername}
          >
            <div className="emoji-bar-username-input">
              <input
                ref={ref => (this.inputElement = ref)}
                data-testid="anon-reaction-name-input"
                type="text"
                className={classNames({
                  'emoji-bar-username-input-error': showInvalidUsername,
                })}
                placeholder="Your name"
                onKeyDown={this.onKeyDown}
                onChange={this.onInputChange}
                onBlur={this.onBlur}
              />
            </div>
          </Transition>
        </div>
      </HideDuringDowntime>
    );
  }

  componentDidUpdate(prevProps) {
    const { volumeControlActive } = this.props;
    const { volumeControlActive: prevVolumeControlActive } = prevProps;

    if (volumeControlActive && !prevVolumeControlActive) {
      const videoContainerEl = document.querySelector(
        '.video-player-video-container'
      );

      if (
        videoContainerEl &&
        videoContainerEl.clientWidth <
          VIDEO_PLAYER_WIDTH_HIDE_EMOJIS_ON_VOLUME_ACTIVE
      ) {
        this.setState({ volumeControlOverEmojiBar: true });
      }
    } else if (!volumeControlActive && prevVolumeControlActive) {
      this.setState({ volumeControlOverEmojiBar: false });
    }
  }

  createEmojiReaction = reaction => {
    this.props.onClickEmoji({
      type: reaction.type,
      time: this.props.currentVideoTime * 1000,
    });

    if (!this.props.isLoggedIn && !getAnonUserName()) {
      if (this.props.useNewAnonymousReactionMode) {
        this.props.onEnterAnonymousReactionMode();
      } else {
        this.setState({
          showUsernameInput: true,
          showEmojiBar: false,
        });
      }
    }
  };

  showUsername = () => {
    anime({
      targets: this.inputElement.parentNode,
      opacity: [0, 1],
      duration: 300,
      easing: 'linear',
    }).finished.then(() => {
      this.inputElement.focus();
    });
  };

  hideUsername = () => {
    return anime({
      targets: this.inputElement.parentNode,
      opacity: [1, 0],
      duration: 300,
      easing: 'linear',
    }).finished.then(() => {
      this.setState({
        showEmojiBar: true,
      });
    });
  };

  onBlur = () => {
    window.clearTimeout(this.onChangeDebounceTimer);

    this.changeUsername(event.target.value);
  };

  onKeyDown = event => {
    if (event.key == 'Enter') {
      window.clearTimeout(this.onChangeDebounceTimer);

      this.changeUsername(event.target.value);
    }

    if (event.key == 'Escape') {
      this.setState({
        showUsernameInput: false,
        showInvalidUsername: false,
      });
    }
  };

  onInputChange = event => {
    const DEBOUNCE_TYPING_TIMEOUT = 1000;
    // in ms

    const name = event.target.value; // SyntheticEvent is pooled/volatile so save value

    // Faster feedback for validity
    this.setState({
      showInvalidUsername: isInvalidName(name),
    });

    window.clearTimeout(this.onChangeDebounceTimer);

    this.onChangeDebounceTimer = window.setTimeout(() => {
      this.changeUsername(name);
    }, DEBOUNCE_TYPING_TIMEOUT);
  };

  changeUsername = name => {
    const validName = !isInvalidName(name);

    this.setState({
      showInvalidUsername: !validName,
      showUsernameInput: !validName,
    });

    if (name && validName) {
      this.props.onChangeUsername(name);
    }
  };
}

EmojiBar.propTypes = {
  hideReaction: PropTypes.string,
  onClickEmoji: PropTypes.func,
};

const mapStateToProps = state => ({
  controlsActive: state.video_player.controlsActive,
  currentVideoTime: state.video_player.current_time,
  duration: state.video_player.duration,
  isLoggedIn: state.user.is_logged_in,
  volumeControlActive: state.video_player.volume_control_active,
  commentsEnabled: state.video_player.video.comments_enabled,
  emojiEnabled: state.video_player.video.use_emojis,
  useNewAnonymousReactionMode:
    (state.feature_flags[ROLLOUT_NEW_ANONYMOUS_REACTION_NAME_INPUT] || {})
      .value === true,
});

const mapDispatchToProps = dispatch => ({
  getFeatureFlag: featureFlag => dispatch(getFeatureFlagCreator(featureFlag)),
  onClickEmoji: reaction => dispatch(createReaction(reaction)),
  onChangeUsername: name => dispatch(updateUsername(name)),
  onEnterAnonymousReactionMode: () =>
    dispatch(videoPlayerUpdateAnonymousReactionMode(true)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EmojiBar);
